import { useEffect, useState } from 'react';
import GlassCard from '../components/GlassCard';
import { mockWeatherData } from '../data/mockWeather';
import { fetchShowcaseWeather } from '../services/weather';
import type {
  HourlyForecast,
  WeatherCondition,
  WeatherData,
} from '../types/weather';
import './WeatherDashboard.css';

/* ═══════════════════════════════════════════════════════════════════════════
   Weather Dashboard — TV Showcase screen
   Visual language ported from rptv-main's WeatherScreen.
   Data-driven via WeatherData interface (mock now, live API later).
   ═══════════════════════════════════════════════════════════════════════════ */

// ── Props ──────────────────────────────────────────────────────────────────
interface WeatherDashboardProps {
  orgName: string;
  logoUrl: string | null;
  primaryColor: string;
  eventName: string | null;
  circuitName: string | null;
  countryCode: string | null;
  timezone: string | null;
  circuitLatitude: number | null;
  circuitLongitude: number | null;
}

// ── Weather condition → background + theme class ───────────────────────────
function getWeatherScene(
  condition: WeatherCondition,
  timezone: string | null,
): { gradient: string; theme: string } {
  // Determine if it's night at the event location
  const tz = timezone ?? undefined;
  let isNight = false;
  try {
    const hour = parseInt(
      new Date().toLocaleString('en-US', {
        hour: 'numeric',
        hour12: false,
        timeZone: tz,
      }),
      10,
    );
    isNight = hour >= 20 || hour < 6;
  } catch {
    // Invalid timezone — assume daytime
  }

  if (isNight) {
    return {
      gradient:
        'linear-gradient(135deg, #0a0a1a 0%, #0d1b2a 30%, #1b2838 60%, #0a0a1a 100%)',
      theme: 'wd--night',
    };
  }

  switch (condition) {
    case 'sunny':
      return {
        gradient:
          'linear-gradient(135deg, #1a3a5c 0%, #2d6aa0 25%, #4a90c4 50%, #87ceeb 80%, #c8e6f5 100%)',
        theme: 'wd--sunny',
      };
    case 'rainy':
      return {
        gradient:
          'linear-gradient(135deg, #1a1a2e 0%, #2d3748 30%, #4a5568 60%, #718096 100%)',
        theme: 'wd--rainy',
      };
    case 'cloudy':
    default:
      return {
        gradient:
          'linear-gradient(135deg, #2d3748 0%, #4a5568 30%, #718096 60%, #a0aec0 100%)',
        theme: 'wd--cloudy',
      };
  }
}

// ── Weather icon per condition ─────────────────────────────────────────────
function WeatherIcon({
  condition,
  size = 48,
}: {
  condition: WeatherCondition;
  size?: number | string;
}) {
  if (condition === 'sunny') {
    return (
      <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
        <circle cx="24" cy="24" r="18" fill="rgba(255,180,0,0.18)" />
        <circle cx="24" cy="24" r="11" fill="#FFB800" />
        {[0, 45, 90, 135, 180, 225, 270, 315].map((a) => (
          <line
            key={a}
            x1={24 + 13 * Math.cos((a * Math.PI) / 180)}
            y1={24 + 13 * Math.sin((a * Math.PI) / 180)}
            x2={24 + 20 * Math.cos((a * Math.PI) / 180)}
            y2={24 + 20 * Math.sin((a * Math.PI) / 180)}
            stroke="#FFB800"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        ))}
      </svg>
    );
  }
  if (condition === 'cloudy') {
    return (
      <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
        <path
          d="M12 30a8 8 0 1 1 0-16c.6 0 1.2.07 1.77.2A10 10 0 0 1 34 20a8 8 0 0 1 0 10H12z"
          fill="white"
          opacity="0.8"
        />
      </svg>
    );
  }
  // rainy / night
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <path
        d="M10 24a8 8 0 1 1 0-14c.5 0 1 .05 1.5.14A9 9 0 0 1 32 16a8 8 0 0 1 0 8H10z"
        fill="#a0b8c8"
        opacity="0.9"
      />
      {[18, 24, 30].map((x) => (
        <line
          key={x}
          x1={x}
          y1="32"
          x2={x - 4}
          y2="42"
          stroke="#6fa8c8"
          strokeWidth="2.5"
          strokeLinecap="round"
        />
      ))}
    </svg>
  );
}

// ── Wind arrow icon ────────────────────────────────────────────────────────
function WindArrow({ degrees }: { degrees: number }) {
  return (
    <span className="wd-wind-badge">
      <svg
        width="100%"
        height="100%"
        viewBox="-8 -8 48 48"
        fill="none"
        style={{ display: 'block' }}
      >
        <g transform={`rotate(${degrees - 90}, 16, 12.5)`}>
          <path
            d="M31.7716 14.5736L2.31576 25.3664C0.751424 25.937 -0.631867 24.1715 0.303526 22.7898L6.44746 13.7251C6.83521 13.1522 6.84089 12.4012 6.46187 11.8199L0.457037 2.66487C-0.456047 1.27056 0.95117 -0.476482 2.50668 0.120259L31.7973 11.3549C33.2779 11.9287 33.262 14.0288 31.7716 14.5736Z"
            fill="white"
          />
        </g>
      </svg>
    </span>
  );
}

// ── Temperature trend line ─────────────────────────────────────────────────
function TrendLine({
  hourly,
  accentColor,
}: {
  hourly: HourlyForecast[];
  accentColor: string;
}) {
  const temps = hourly.map((h) => h.temp);
  const min = Math.min(...temps);
  const max = Math.max(...temps);
  const range = max - min || 1;
  const W = 1000;
  const H = 100;
  const padY = 16;
  const n = temps.length;

  const pts = temps.map((t, i) => {
    const x = ((i + 0.5) / n) * W;
    const y = H - ((t - min) / range) * (H - padY * 2) - padY;
    return { x, y };
  });

  let pathD = `M${pts[0].x},${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const prev = pts[i - 1];
    const curr = pts[i];
    const cpX = (prev.x + curr.x) / 2;
    pathD += ` C${cpX},${prev.y} ${cpX},${curr.y} ${curr.x},${curr.y}`;
  }

  const dot = (p: { x: number; y: number }) => ({
    left: `${(p.x / W) * 100}%`,
    top: `${(p.y / H) * 100}%`,
    transform: 'translate(-50%, -50%)',
  });

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        style={{ width: '100%', height: '100%', display: 'block' }}
      >
        <defs>
          <linearGradient id="trendGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={accentColor} stopOpacity="0.9" />
            <stop offset="100%" stopColor={accentColor} stopOpacity="0.6" />
          </linearGradient>
        </defs>
        <path
          d={pathD}
          stroke="url(#trendGrad)"
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span
        style={{
          position: 'absolute',
          width: '1.2rem',
          height: '1.2rem',
          borderRadius: '50%',
          background: accentColor,
          opacity: 0.95,
          ...dot(pts[0]),
        }}
      />
      <span
        style={{
          position: 'absolute',
          width: '1.2rem',
          height: '1.2rem',
          borderRadius: '50%',
          background: accentColor,
          opacity: 0.7,
          ...dot(pts[pts.length - 1]),
        }}
      />
    </div>
  );
}

// ── Live clock in event timezone ───────────────────────────────────────────


// ── Stat card ──────────────────────────────────────────────────────────────
function StatCard({
  icon,
  label,
  value,
  unit,
  sub,
}: {
  icon: string;
  label: string;
  value: string | number;
  unit: string;
  sub?: string;
}) {
  return (
    <GlassCard className="wd-stat-card">
      <div className="wd-stat-card__label">
        <span className="wd-stat-card__icon">{icon}</span>
        {label}
      </div>
      <div className="wd-stat-card__value">
        {value}
        <span className="wd-stat-card__unit">{unit}</span>
      </div>
      {sub && <div className="wd-stat-card__sub">{sub}</div>}
    </GlassCard>
  );
}

// ── Main Weather Dashboard ─────────────────────────────────────────────────
export default function WeatherDashboard({
  orgName,
  logoUrl,
  primaryColor,
  eventName,
  circuitName,
  countryCode,
  timezone,
  circuitLatitude,
  circuitLongitude,
}: WeatherDashboardProps) {
  const [data, setData] = useState<WeatherData>(mockWeatherData);

  useEffect(() => {
    if (circuitLatitude == null || circuitLongitude == null) {
      return;
    }

    let cancelled = false;

    fetchShowcaseWeather(circuitLatitude, circuitLongitude)
      .then((weather) => {
        if (!cancelled) {
          setData(weather);
        }
      })
      .catch((error) => {
        console.warn('[WeatherDashboard] Live weather unavailable, using fallback data:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [circuitLatitude, circuitLongitude]);

  const { gradient, theme } = getWeatherScene(data.condition, timezone);



  return (
    <div
      className={`wd-root ${theme}`}
      style={
        {
          background: gradient,
        } as React.CSSProperties
      }
    >
      {/* Top dark gradient overlay for text legibility */}
      <div className="wd-top-gradient" />

      {/* Inner layout container */}
      <div className="wd-inner">


        {/* ── Body ── */}
        <div className="wd-body">
          {/* Left: big temperature + condition + hourly forecast */}
          <div className="wd-body__left">
            <div className="wd-temp-block">
              <div className="wd-temp">{data.currentTemp}°F</div>
              <div className="wd-condition">
                {data.conditionLabel ??
                  data.condition.charAt(0).toUpperCase() +
                    data.condition.slice(1)}
              </div>
              <div className="wd-hilo">
                H:{data.high}°&nbsp;&nbsp;L:{data.low}°
              </div>
            </div>

            {/* Hourly forecast strip */}
            <div className="wd-forecast">
              {/* Temps row */}
              <div className="wd-forecast__temps">
                {data.hourly.map((slot) => (
                  <span key={slot.time} className="wd-forecast__temp-val">
                    {slot.temp}°
                  </span>
                ))}
              </div>

              {/* Trend line */}
              <div className="wd-forecast__chart">
                <TrendLine hourly={data.hourly} accentColor={primaryColor} />
              </div>

              {/* Icons + wind + time */}
              <div className="wd-forecast__slots">
                {data.hourly.map((slot) => (
                  <div key={slot.time} className="wd-hourly-col">
                    <div className="wd-hourly__icon">
                      <WeatherIcon condition={slot.condition} size="100%" />
                    </div>
                    <div className="wd-hourly__wind">
                      <WindArrow degrees={slot.windDirection} />
                      <span>{slot.windSpeed}mph</span>
                    </div>
                    <div className="wd-hourly__time">{slot.time}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: stat cards */}
          <div className="wd-body__right">
            <StatCard icon="💧" label="Humidity" value={data.humidity} unit="%" />
            <StatCard
              icon="🌬️"
              label="Wind"
              value={data.windSpeed}
              unit=" mph"
              sub={`▶ ${data.windDirection} ~${data.windDegrees}°`}
            />
            <StatCard
              icon="🌧️"
              label="Rainfall"
              value={data.rainfall}
              unit="%"
              sub={`${data.rainfallIn} in`}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
